import { gsap } from "gsap";
import {
  getExtendedViewportInfo,
  getViewportOrientation,
  mediaQueryFrom,
} from "../breakpoints";
export const SELECTORS: Record<string, string> = {
  bg: ".mediatext__media-bg",
  media: ".mediatext__figure",
  headline: ".mediatext__headline",
  copy: ".mediatext__copy",
  horizontals: '.scrollslide[data-animation-type="fade-horizontal"]',
  slide: ".scrollslide",
};

export const clampShift = (
  section: HTMLElement,
  ratio: number,
  min: number,
  max: number
) => {
  const basis = Math.min(section.clientWidth || 0, window.innerWidth);
  return gsap.utils.clamp(min, max, basis * ratio);
};

export const computeTextShifts = (section: HTMLElement) => ({
  headline: clampShift(section, 0.18, 80, 260),
  copy: clampShift(section, 0.08, 40, 160),
});

export const inProps = { opacity: 1, duration: 0.4 };
export const outProps = { opacity: 0, duration: 0.4 };

const SAFE_MEDIA_TEXT_GAP = 14;

const getPreferredLeftInRight = () => {
  const viewportInfo = getExtendedViewportInfo();
  const orientation = getViewportOrientation();

  let rightPos = "16%";
  if (viewportInfo.upto === "tablet") rightPos = "-5%";
  if (viewportInfo.between === "tablet-desktop") {
    if (orientation === "landscape") rightPos = "0";
    if (orientation === "portrait") rightPos = "-40%";
  }
  if (viewportInfo.from === "widescreen" && orientation === "landscape") {
    rightPos = "18%";
  }
  if (viewportInfo.from === "ultrawide" && orientation === "portrait") {
    rightPos = "-4%";
  }

  return rightPos;
};

const resolveRightOffset = (value: string, element: HTMLElement) => {
  const numericValue = Number.parseFloat(value);
  if (!Number.isFinite(numericValue)) return 0;

  if (value.endsWith("%")) {
    const containingBlock =
      element.offsetParent instanceof HTMLElement
        ? element.offsetParent
        : element.parentElement;
    const width = containingBlock?.clientWidth || window.innerWidth;
    return (width * numericValue) / 100;
  }

  if (value.endsWith("rem")) {
    const rootFontSize = Number.parseFloat(
      window.getComputedStyle(document.documentElement).fontSize
    );
    return numericValue * (Number.isFinite(rootFontSize) ? rootFontSize : 16);
  }

  return numericValue;
};

const isSideBySideMediaTextLayout = () =>
  window.matchMedia(
    `${mediaQueryFrom("laptop")} and (orientation: landscape), ${mediaQueryFrom(
      "desktop"
    )}`
  ).matches;

const getStackedLeftInRight = (section: HTMLElement) => {
  const figure = section.querySelector<HTMLElement>(SELECTORS.media);
  const visualElement =
    section.querySelector<HTMLElement>(".mediatext__image") ?? figure;
  const mediaContainer = figure?.parentElement;
  if (!figure || !visualElement || !mediaContainer) return "0";

  const previousRight = figure.style.right;
  figure.style.right = "0px";

  const visualRect = visualElement.getBoundingClientRect();
  const containerRect = mediaContainer.getBoundingClientRect();
  const visualCenter = visualRect.left + visualRect.width / 2;
  const containerCenter = containerRect.left + containerRect.width / 2;

  figure.style.right = previousRight;

  return `${visualCenter - containerCenter}px`;
};

const matchesLaptopRange = () =>
  window.matchMedia(mediaQueryFrom("laptop")).matches &&
  !window.matchMedia(mediaQueryFrom("desktop")).matches;

const getRightInLeft = () =>
  window.matchMedia(mediaQueryFrom("laptop")).matches ? "-4rem" : "0";

const getCopyInLeft = () => (matchesLaptopRange() ? "-2rem" : 0);

const getFinalTextLeft = (section: HTMLElement) => {
  const headline = section.querySelector<HTMLElement>(SELECTORS.headline);
  const copy = section.querySelector<HTMLElement>(SELECTORS.copy);
  const textElements = section.querySelectorAll<HTMLElement>(
    `${SELECTORS.headline} .mediatext__line, ${SELECTORS.copy}`
  );
  const previousHeadlineLeft = headline?.style.left;
  const previousHeadlineTransform = headline?.style.transform;
  const previousCopyLeft = copy?.style.left;
  const previousCopyTransform = copy?.style.transform;

  if (headline) {
    headline.style.left = getRightInLeft();
    headline.style.transform = "none";
  }
  if (copy) {
    copy.style.left = `${getCopyInLeft()}`;
    copy.style.transform = "none";
  }

  const left = Math.min(
    ...Array.from(textElements)
      .filter((element) => element.offsetParent !== null)
      .map((element) => element.getBoundingClientRect().left)
  );

  if (headline) {
    headline.style.left = previousHeadlineLeft ?? "";
    headline.style.transform = previousHeadlineTransform ?? "";
  }
  if (copy) {
    copy.style.left = previousCopyLeft ?? "";
    copy.style.transform = previousCopyTransform ?? "";
  }

  return Number.isFinite(left) ? left : window.innerWidth;
};

const getSafeLeftInRight = (section: HTMLElement, preferredRight: string) => {
  if (!isSideBySideMediaTextLayout()) return preferredRight;

  const figure = section.querySelector<HTMLElement>(SELECTORS.media);
  const image = section.querySelector<HTMLElement>(".mediatext__image");
  const visualElement = image ?? figure;
  if (!figure || !visualElement) return preferredRight;

  const preferredRightPx = resolveRightOffset(preferredRight, figure);
  const previousRight = figure.style.right;
  figure.style.right = "0px";

  const imageRightAtZero = visualElement.getBoundingClientRect().right;
  const textLeft = getFinalTextLeft(section);
  const minSafeRightPx = imageRightAtZero - textLeft + SAFE_MEDIA_TEXT_GAP;

  figure.style.right = previousRight;

  return `${Math.max(preferredRightPx, minSafeRightPx)}px`;
};

const getLeftInRight = (section?: HTMLElement) => {
  if (section && !isSideBySideMediaTextLayout()) {
    return getStackedLeftInRight(section);
  }

  const preferredRight = getPreferredLeftInRight();
  return section ? getSafeLeftInRight(section, preferredRight) : preferredRight;
};

export const getLeftInProps = (section?: HTMLElement) => {
  return {
    right: getLeftInRight(section),
    ...inProps,
  };
};

export const getLeftOutProps = (section?: HTMLElement) => {
  const figure = section?.querySelector<HTMLElement>(SELECTORS.media);

  if (!section || !figure) {
    return {
      right: "5%",
      ...outProps,
    };
  }

  const right = getLeftInRight(section);
  const rightPx = resolveRightOffset(right, figure);
  const shiftPx = clampShift(section, 0.08, 80, 180);

  return {
    right: `${rightPx + shiftPx}px`,
    ...outProps,
  };
};

export const getRightInProps = () => ({
  left: getRightInLeft(),
  ...inProps,
});

export const getCopyInProps = () => ({
  left: getCopyInLeft(),
  ...inProps,
});

export const getHeadlineOutProps = () => ({
  left: window.matchMedia(mediaQueryFrom("laptop")).matches ? "-4rem" : "5%",
  opacity: 0,
  duration: 0.4,
});
